/**
 * Status de ordem e de quebra.
 *
 * São TEXTO, não número. A API (`producaoapi`) valida `status` com
 * `z.enum(['RASCUNHO','ABERTA','EM_ANDAMENTO','CONCLUIDA','CANCELADA'])` e o
 * banco novo tem CHECK com os mesmos cinco valores em `production_order` e em
 * `production_break`. Os inteiros do legado (`-2`, `1`, `2`, `5`, …) não
 * sobreviveram à migração — mandar `status=5` devolve 400 VALIDATION_ERROR.
 *
 * O rótulo em português vem do campo `statusLabel` da API; o daqui é fallback.
 */
export const ORDER_STATUS = {
  RASCUNHO: { label: 'Rascunho', color: 'default' },
  ABERTA: { label: 'Aberta', color: 'info' },
  EM_ANDAMENTO: { label: 'Em andamento', color: 'warning' },
  CONCLUIDA: { label: 'Concluída', color: 'success' },
  CANCELADA: { label: 'Cancelada', color: 'error' },
}

export const getOrderStatus = status => {
  const entry = ORDER_STATUS[status]
  return entry || { label: '—', color: 'default' }
}

/** Opções de filtro por status de ordem. */
export const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'RASCUNHO', label: 'Rascunho' },
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'CONCLUIDA', label: 'Concluída' },
  { value: 'CANCELADA', label: 'Cancelada' },
]

/**
 * Estados terminais: a máquina de transições da API não sai deles, então
 * editar ou cancelar uma ordem nesse ponto devolveria 409.
 */
export const TERMINAL_ORDER_STATUS = ['CONCLUIDA', 'CANCELADA']

export const isTerminalOrderStatus = status => TERMINAL_ORDER_STATUS.includes(status)

/** Status da quebra — mesmo vocabulário da ordem, em cor sólida para o Kanban. */
export const BREAK_STATUS = {
  RASCUNHO: { label: 'Rascunho', color: '#757575' },
  ABERTA: { label: 'Aberta', color: '#0288d1' },
  EM_ANDAMENTO: { label: 'Em andamento', color: '#ed6c02' },
  CONCLUIDA: { label: 'Concluída', color: '#2e7d32' },
  CANCELADA: { label: 'Cancelada', color: '#d32f2f' },
}

export const getBreakStatus = status => {
  const entry = BREAK_STATUS[status]
  return entry || { label: String(status ?? '—'), color: '#757575' }
}

/**
 * Status da quebra na paleta de `Chip`/`LinearProgress` do MUI.
 *
 * `BREAK_STATUS` guarda hex porque o Kanban pinta a borda do cartão à mão; o
 * `Chip` precisa do nome da paleta. Duas leituras do mesmo estado, não dois
 * vocabulários.
 */
export const BREAK_STATUS_COLOR = {
  RASCUNHO: 'inherit',
  ABERTA: 'info',
  EM_ANDAMENTO: 'warning',
  CONCLUIDA: 'success',
  CANCELADA: 'error',
}

export const getBreakStatusColor = status => BREAK_STATUS_COLOR[status] || 'inherit'

/** A quebra tem a mesma máquina de estados da ordem. */
export const isTerminalBreakStatus = status => TERMINAL_ORDER_STATUS.includes(status)

/**
 * Ações permitidas a partir do estado atual da quebra.
 *
 * Espelha `canTransition` da API: iniciar só de `ABERTA`, concluir só de
 * `EM_ANDAMENTO`, cancelar de qualquer não-terminal. Mostrar o botão fora
 * disso só produziria 409 na cara do usuário.
 */
export const acoesDaQuebra = status => ({
  iniciar: status === 'ABERTA',
  concluir: status === 'EM_ANDAMENTO',
  cancelar: !isTerminalBreakStatus(status) && Boolean(status),
})

/** Qualidade do apontamento (`break_stage_event.quality_status`). */
export const QUALITY_STATUS = {
  PENDENTE: { label: 'Pendente', color: 'default' },
  APROVADO: { label: 'Aprovado', color: 'success' },
  REPROVADO: { label: 'Reprovado', color: 'error' },
  RETRABALHO: { label: 'Retrabalho', color: 'warning' },
}

export const getQualityStatus = status =>
  QUALITY_STATUS[status] || { label: status || '—', color: 'default' }

/**
 * Situação da etiqueta como a API grava: TEXTO em caixa alta com underline.
 *
 * `RFID_STATUS` abaixo lista os rótulos já traduzidos e serve às telas que
 * filtram por texto legível; este mapa é para o dado cru de `/rfid-items`.
 */
export const RFID_SITUATION = {
  CRIADO: { label: 'Criado', color: 'default' },
  IMPRESSO: { label: 'Impresso', color: 'info' },
  AGUARDANDO_PRODUCAO: { label: 'Aguardando produção', color: 'warning' },
  EM_PRODUCAO: { label: 'Em produção', color: 'warning' },
  EM_CONFERENCIA: { label: 'Em conferência', color: 'warning' },
  FINALIZADO: { label: 'Finalizado', color: 'success' },
  EXPEDIDO: { label: 'Expedido', color: 'success' },
  CANCELADO: { label: 'Cancelado', color: 'error' },
  PERDIDO: { label: 'Perdido', color: 'error' },
  BLOQUEADO: { label: 'Bloqueado', color: 'error' },
}

export const getRfidSituation = situation =>
  RFID_SITUATION[situation] || { label: situation || '—', color: 'default' }

/** Situações possíveis de um RFID (Épico 04) — rótulos como a API os devolve. */
export const RFID_STATUS = [
  { value: 'Criado', color: 'default' },
  { value: 'Impresso', color: 'info' },
  { value: 'Aguardando produção', color: 'warning' },
  { value: 'Em produção', color: 'warning' },
  { value: 'Em conferência', color: 'warning' },
  { value: 'Finalizado', color: 'success' },
  { value: 'Expedido', color: 'success' },
  { value: 'Cancelado', color: 'error' },
  { value: 'Perdido', color: 'error' },
  { value: 'Bloqueado', color: 'error' },
]

export const getRfidStatusColor = status => {
  const entry = RFID_STATUS.find(item => item.value === status)
  return entry ? entry.color : 'default'
}

/** Etapas padrão sugeridas no Kanban quando não há fluxo configurado. */
export const DEFAULT_STAGES = [
  'Criada',
  'Impressão',
  'Produção',
  'Costura',
  'Acabamento',
  'Conferência',
  'Embalagem',
  'Finalizada',
]
